import { usePublicConfig } from "../../Public/usePublicConfig";
/**
 * DocumentAgentLGChat
 *
 * Chat interface for the TipTap Document Agent.
 * Integrates with document context and SSE streaming for TipTap operations.
 */

import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import {
  Button,
  Form,
  Dropdown,
  OverlayTrigger,
  Tooltip,
  Modal,
  Spinner,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDocumentContext } from "./DocumentAgentLGContext";
import { useLanguageContext } from "../../Context/LanguageContext";
import { ChatMessage } from "./DocumentAgentLGTypes";
import {
  createSSEProcessorTipTap,
  SSEProcessorTipTapCallbacks,
} from "./Tool/SSEProcessorTipTap";
import { createTipTapUpdateStrategy } from "./Tool/TipTapDocumentUpdateStrategy";
import ShareableReportModal from "../ShareableReportModal";
import {
  ensureChatExists,
  ChatInfo,
  ChatManagementHooks,
} from "../DocumentAgent/Tool/ChatManagement";
import {
  MessageItem,
  LoadingIndicator,
  WelcomeMessage,
  Message,
  CollapsedToolGroup,
} from "../DocumentAgent/Tool/MessageComponents";
import { ThinkingBlock } from "../DocumentAgent/Tool/ThinkingBlock";
import { useScrollManager } from "../DocumentAgent/Hooks/useScrollManager";
import {
  extractChoiceIds,
  parseMultipleChoice,
} from "../DocumentAgent/Tool/parseMultipleChoice";
import { handleFetchUnauthorized } from "../../Utils/handleFetchUnauthorized";
import PinnedSkills from "./PinnedSkills";
import ContextGauge from "./ContextGauge";
import { usePortal } from "../../Portal/PortalContext";
import { AGENT_CONFIGS } from "./agentEndpoints";
import type { AgentType } from "./agentEndpoints";
import "../DocumentAgent/CSS/DocumentAgentChat.css";

// ============================================================================
// TYPES
// ============================================================================

interface DocumentAgentLGChatProps {
  workproductId: string;
  workproductType?: string;
  chatId?: string;
  messages: ChatMessage[];
  availableChats: ChatInfo[];
  isConversationLoading?: boolean;
  onChatIdChange?: (chatId: string | null) => void;
  onNewMessage?: (message: ChatMessage) => void;
  onRefreshChats?: () => void;
  setActiveRightTab?: (tab: string) => void;
  onDocumentActivated?: (docId: string, documentName: string) => void;
  inputDisabled?: boolean;
  inputDisabledMessage?: string;
  readOnly?: boolean;
  onImportDocxAsNewDocument?: (file: File) => Promise<void>;
  onImportPdfAsNewDocument?: (file: File) => Promise<void>;
  /** Default agent type — allows domain wrappers to pre-select an agent (e.g. "patent-draft") */
  defaultAgentType?: AgentType;
  /**
   * Default agent invocation mode for this workproduct type. The user can
   * still switch via the Ask/Plan/Execute dropdown — this only sets the
   * initial value.
   *   "ask"  → read-only Q&A (good for Patent Review, FTO research)
   *   "plan" → agent proposes changes for approval
   *   "edit" → agent executes directly (Patent Draft, OA Response)
   *
   * Defaults to "edit" to preserve existing behaviour for any caller
   * that doesn't specify.
   */
  defaultAgentMode?: "ask" | "plan" | "edit";
  /** Domain-specific context (e.g. { patentOffice, patentType }) merged into agent request body */
  domainContext?: Record<string, any>;
  /** Called after images are uploaded to GCS (before sending to agent). Receives uploaded image info. */
  onImagesUploaded?: (images: { gcsPath: string; proxyUrl: string }[]) => void;
  /** Replaces the default welcome message shown when the chat is empty. */
  welcomeMessageOverride?: React.ReactNode;
  /**
   * Extra action button(s) rendered with the chat input controls
   * (e.g. "Verify by Lawyer"). Pass a function to receive chat state —
   * e.g. disable the action until the user has sent a message.
   */
  extraInputActions?:
    | React.ReactNode
    | ((ctx: {
        hasUserMessages: boolean;
        initialRequest?: string;
        isBusy: boolean;
        hasUnsavedDocuments: boolean;
      }) => React.ReactNode);
  /** Rendered after the last chat message (e.g. the attorney-review offer). */
  messageFooter?:
    | React.ReactNode
    | ((ctx: {
        hasUserMessages: boolean;
        initialRequest?: string;
        isBusy: boolean;
        hasUnsavedDocuments: boolean;
      }) => React.ReactNode);
  /** Hide just the share-report button (lighter than minimalChrome). */
  hideShareButton?: boolean;
  /** Hide just the Ask/Plan/Execute mode selector; the agent stays in defaultAgentMode. */
  hideModeSelector?: boolean;
  consumerMode?: boolean;
  initialMessage?: string;
  onInitialMessageConsumed?: () => void;
}

// ── Agent type configuration ─────────────────────────────────────────
// Defined in agentEndpoints.ts so tests can import without pulling in React deps.
export type { AgentType };
export { AGENT_CONFIGS };

// ============================================================================
// COMPONENT
// ============================================================================

const DocumentAgentLGChat: React.FC<DocumentAgentLGChatProps> = ({
  workproductId,
  workproductType = "TipTap Document Agent",
  chatId,
  messages,
  availableChats,
  isConversationLoading = false,
  onChatIdChange,
  onNewMessage,
  onRefreshChats,
  setActiveRightTab,
  onDocumentActivated,
  inputDisabled = false,
  inputDisabledMessage,
  readOnly = false,
  onImportDocxAsNewDocument,
  onImportPdfAsNewDocument,
  defaultAgentType,
  defaultAgentMode = "edit",
  domainContext: domainContextProp,
  onImagesUploaded,
  welcomeMessageOverride,
  extraInputActions,
  messageFooter,
  hideShareButton = false,
  hideModeSelector = false,
  consumerMode = false,
  initialMessage,
  onInitialMessageConsumed,
}) => {
  const { t, i18n } = useTranslation();
  // Customers see that an answer is being prepared, not the saved reasoning.
  const hideReasoning = usePortal().access?.accountType === "customer";
  const initialSent = useRef(false);
  const preparingSend = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const [initialError, setInitialError] = useState("");
  const { currentLanguage } = useLanguageContext();

  // Local UI state
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [textareaRows, setTextareaRows] = useState(4);
  const [isComposing, setIsComposing] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Chat management state

  // File upload state
  const [isUploading, setIsUploading] = useState(false);
  const [selectedImages, setSelectedImages] = useState<
    { file: File; previewUrl: string }[]
  >([]);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const docxInputRef = useRef<HTMLInputElement | null>(null);
  const pdfInputRef = useRef<HTMLInputElement | null>(null);

  // Sharing state
  const [isPublic, setIsPublic] = useState(false);
  const [isTogglingShare, setIsTogglingShare] = useState(false);
  const publicConfig = usePublicConfig();
  const betaMode = publicConfig?.beta?.enabled !== false;
  const [selectedModel, setSelectedModel] = useState<
    | "gpt-5.6-sol"
    | "gpt-5.6-terra"
    | "gpt-5.6-luna"
    | "gemini-3.7-flash"
    | "kimi-k3"
    | "glm-5.3"
    | "qwen3.8-max"
    | "deepseek-v4-pro"
    | "claude-opus-5"
    | "claude-sonnet-5"
  >("gpt-5.6-terra");
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [showUploadDropdown, setShowUploadDropdown] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);

  // Agent invocation mode — single source of truth, three exclusive
  // values:
  //   "ask"  → backend mode: "qa"   (read-only, no edits, no proposals)
  //   "plan" → backend mode: "plan" (records proposals for user review)
  //   "edit" → backend mode: omitted/"execute" (full edit power, default)
  // Persists for the whole session until the user picks another mode.
  // The default is configurable per workproduct (e.g. "ask" for Patent
  // Review, "edit" for Patent Draft) via `defaultAgentMode`.
  const [agentMode, setAgentMode] = useState<"ask" | "plan" | "edit">(
    defaultAgentMode
  );
  const [showAgentModeDropdown, setShowAgentModeDropdown] = useState(false);
  // Pending proposals fetched after a plan-mode turn ends. null = no
  // panel visible; [] = panel visible but empty (rare).
  const [pendingProposals, setPendingProposals] = useState<Array<{
    id: number;
    toolName: string;
    args: any;
    rationale: string | null;
  }> | null>(null);
  const [proposalsActionInFlight, setProposalsActionInFlight] = useState(false);

  // Overflow detection for chat controls
  const [showChatOverflow, setShowChatOverflow] = useState(false);
  const [chatControlsHidden, setChatControlsHidden] = useState(false);
  const [modelSwitcherHidden, setModelSwitcherHidden] = useState(false);
  const actionsRowRef = useRef<HTMLDivElement>(null);
  const chatOverflowRef = useRef<HTMLDivElement>(null);

  // Agent type is fixed based on workproduct type (no user switching)
  const agentType: AgentType = defaultAgentType || "base";
  // Minimal chrome (Legal Q&A v2 intake): hide skills, share, new-chat, and the
  // ask/plan/execute mode selector. Upload stays (it's the only way to add
  // documents when the document panel is hidden). Configured in agentEndpoints.
  const minimalChrome = AGENT_CONFIGS[agentType]?.minimalChrome === true;

  // Resolve extra input actions; function form receives chat state so callers
  // can gate actions (e.g. disable "Verify by Lawyer" until the user has
  // described their matter).
  const hasUserMessages = messages.some(m => m?.role === "user");

  // Model configuration
  type ModelKey = typeof selectedModel;

  const MODEL_DISPLAY_NAMES: Record<ModelKey, string> = {
    "claude-sonnet-5": "Claude Sonnet 5",
    "claude-opus-5": "Claude Opus 5",
    "gpt-5.6-sol": "GPT-5.6 Sol",
    "gpt-5.6-terra": "GPT-5.6 Terra",
    "gpt-5.6-luna": "GPT-5.6 Luna",
    "kimi-k3": "Kimi K3",
    "glm-5.3": "GLM-5.3",
    "qwen3.8-max": "Qwen 3.8 Max",
    "deepseek-v4-pro": "DeepSeek V4 Pro",
    "gemini-3.7-flash": "Gemini 3.7 Flash",
  };

  const MODEL_DESCRIPTIONS: Record<ModelKey, string> = {
    "claude-sonnet-5": t("models.claude-sonnet-5"),
    "claude-opus-5": t("models.claude-opus-5"),
    "gpt-5.6-sol": t("models.gpt-5_6-sol"),
    "gpt-5.6-terra": t("models.gpt-5_6-terra"),
    "gpt-5.6-luna": t("models.gpt-5_6-luna"),
    "kimi-k3": t("models.kimi-k3"),
    "glm-5.3": t("models.glm-5_3"),
    "qwen3.8-max": t("models.qwen3_8-max"),
    "deepseek-v4-pro": t("models.deepseek-v4-pro"),
    "gemini-3.7-flash": t("models.gemini-3_7-flash"),
  };

  const MODEL_ORDER: ModelKey[] = [
    "claude-sonnet-5",
    "claude-opus-5",
    "gpt-5.6-sol",
    "gpt-5.6-terra",
    "gpt-5.6-luna",
    "kimi-k3",
    "glm-5.3",
    "qwen3.8-max",
    "deepseek-v4-pro",
    "gemini-3.7-flash",
  ];

  // Quota cost multipliers shown in the picker tooltip so users can gauge
  // relative expense. Keep in sync with server/src/Payment/planConfig.ts
  // MODEL_COST_COEFFICIENTS (the billing source of truth).
  const MODEL_COST_COEFFICIENTS: Record<ModelKey, number> = {
    "claude-sonnet-5": 0.84,
    "claude-opus-5": 2.0,
    "gpt-5.6-sol": 1.68,
    "gpt-5.6-terra": 1.0,
    "gpt-5.6-luna": 0.1,
    "kimi-k3": 1.26,
    "glm-5.3": 0.3,
    "qwen3.8-max": 0.53,
    "deepseek-v4-pro": 0.08,
    "gemini-3.7-flash": 0.32,
  };

  // Scroll state
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [streamingStarted, setStreamingStarted] = useState(false);

  // Multiple choice sequential state
  // Tracks which message has active pending choices, current index, and collected answers
  const [activePendingChoices, setActivePendingChoices] = useState<{
    messageId: string;
    choices: Array<{ id: string; question: string; allowMultiple: boolean }>;
    currentIndex: number;
    collectedAnswers: Map<string, string[]>;
  } | null>(null);

  // Scroll management hook
  const {
    messageHistoryRef,
    scrollToBottom,
    scrollUserMessageToTop,
    handleScroll,
    debouncedScrollCheck,
  } = useScrollManager(setShowScrollToBottom);

  // Reset scroll-to-bottom indicator when switching chats
  useEffect(() => {
    setShowScrollToBottom(false);
  }, [chatId]);

  // Streaming state - track the current streaming message ID for updates
  const streamingMessageIdRef = useRef<string | null>(null);

  // Persist last-applied patch sequence across messages within the same chat.
  // Keyed by chatId so switching chats gets a fresh counter.
  const patchSeqByChatRef = useRef<Record<string, number>>({});

  // Track the single active todo widget so multiple write_todos calls within
  // one agent turn update the same message instead of creating duplicates.
  const todoMessageIdRef = useRef<string | null>(null);

  // Thinking/reasoning state (for models with extended thinking like Claude)
  const [thinkingContent, setThinkingContent] = useState("");
  const [isThinking, setIsThinking] = useState(false);
  const [thinkingDuration, setThinkingDuration] = useState(0);

  // Context-usage gauge: how full the conversation context is relative to the
  // summarization threshold. Seeded on chat load via the context-usage
  // endpoint, then refreshed by the `contextUsage` SSE event after each turn.
  const [contextUsage, setContextUsage] = useState<{
    contextTokens: number;
    maxContextTokens: number;
  } | null>(null);
  // A model the user picked that would force an immediate summarization on the
  // current conversation (its window is smaller than the current context).
  // Non-null → the confirm modal is shown; commit on confirm.
  const [pendingModelSwitch, setPendingModelSwitch] = useState<ModelKey | null>(
    null
  );
  const thinkingContentRef = useRef(""); // ref to avoid stale closure
  const thinkingDurationRef = useRef(0); // ref for stale closure in finally
  const thinkingAssistantIdRef = useRef<string | null>(null); // which assistant msg the thinking belongs to

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";

  // Seed the context-usage gauge from the persisted checkpointer state when the
  // chat loads or the user switches chats / models. Live updates thereafter
  // arrive via the `contextUsage` SSE event at the end of each turn.
  useEffect(() => {
    // The gauge is hidden in read-only views, so don't bother fetching.
    if (!chatId || !workproductId || readOnly) {
      setContextUsage(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${backendUrl}/api/document-agent/context-usage`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              chatId,
              workproductId,
              agentType,
              model: selectedModel,
            }),
          }
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data?.contextUsage) {
          setContextUsage(data.contextUsage);
        }
      } catch {
        // Non-fatal: the gauge populates after the next turn's SSE event.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [chatId, workproductId, agentType, selectedModel, backendUrl, readOnly]);

  // Model switch with a compaction guard. If the target model's context window
  // can't hold the current conversation, the next turn would auto-summarize, so
  // confirm first. We ask the backend for the target model's budget (single
  // source of truth) rather than duplicating per-model thresholds here.
  const handleModelSelect = useCallback(
    async (modelKey: ModelKey) => {
      setShowModelDropdown(false);
      if (modelKey === selectedModel) return;
      if (chatId && contextUsage && contextUsage.contextTokens > 0) {
        try {
          const res = await fetch(
            `${backendUrl}/api/document-agent/context-usage`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({
                chatId,
                workproductId,
                agentType,
                model: modelKey,
              }),
            }
          );
          if (res.ok) {
            const data = await res.json();
            const cu = data?.contextUsage;
            if (
              cu &&
              typeof cu.contextTokens === "number" &&
              typeof cu.maxContextTokens === "number" &&
              cu.contextTokens > cu.maxContextTokens
            ) {
              setPendingModelSwitch(modelKey); // would compact → confirm first
              return;
            }
          }
        } catch {
          // Non-fatal: fall through and switch (backend still compacts safely).
        }
      }
      setSelectedModel(modelKey);
    },
    [selectedModel, chatId, contextUsage, backendUrl, workproductId, agentType]
  );

  const confirmModelSwitch = useCallback(() => {
    if (pendingModelSwitch) setSelectedModel(pendingModelSwitch);
    setPendingModelSwitch(null);
  }, [pendingModelSwitch]);

  const cancelModelSwitch = useCallback(() => setPendingModelSwitch(null), []);

  // Document context
  const {
    getContextForAgent,
    documentContext: _documentContext,
    addTipTapDocument,
    addPDFDocument,
    addFormDocument,
    updateFormData,
    saveDirtyDocuments,
    getDocumentById,
    applyPatch,
    setAgentWorking,
  } = useDocumentContext();
  const extraActions =
    typeof extraInputActions === "function"
      ? extraInputActions({
          hasUserMessages,
          initialRequest:
            typeof messages.find(m => m.role === "user")?.content === "string"
              ? (messages.find(m => m.role === "user")!.content as string)
              : undefined,
          isBusy: isLoading || isUploading || isConversationLoading,
          hasUnsavedDocuments: Object.values(_documentContext.documents).some(
            d => "isDirty" in d && d.isDirty
          ),
        })
      : extraInputActions;
  const footerContent =
    typeof messageFooter === "function"
      ? messageFooter({
          hasUserMessages,
          initialRequest:
            typeof messages.find(m => m.role === "user")?.content === "string"
              ? (messages.find(m => m.role === "user")!.content as string)
              : undefined,
          isBusy: isLoading || isUploading || isConversationLoading,
          hasUnsavedDocuments: Object.values(_documentContext.documents).some(
            d => "isDirty" in d && d.isDirty
          ),
        })
      : messageFooter;

  // Language normalization
  const effectiveLanguage = useMemo(() => {
    const lang = i18n.language || "en";
    if (lang.toLowerCase().startsWith("zh") || currentLanguage === "中文") {
      return "zh";
    }
    return "en";
  }, [i18n.language, currentLanguage]);

  // Chat management hooks
  const chatHooks: ChatManagementHooks = useMemo(
    () => ({ onChatIdChange, onRefreshChats }),
    [onChatIdChange, onRefreshChats]
  );

  // Update strategy
  const updateStrategy = useMemo(() => createTipTapUpdateStrategy(), []);

  // Compute answered choices from message history and detect pending choices
  // Map from choiceId to the answer value (for display)
  const answeredChoices = useMemo(() => {
    const answered = new Map<string, string>();

    // Track the last assistant message with unanswered choices
    let lastUnansweredMessageId: string | null = null;
    let lastUnansweredChoices: Array<{
      id: string;
      question: string;
      allowMultiple: boolean;
    }> = [];

    // Scan through messages to find which choices have been answered.
    // Scans forward past intermediate assistant/tool messages to find the
    // next user message (needed because ask_user_choice tool results in
    // an intermediate LLM response between the choice and the user's answer).
    for (let i = 0; i < messages.length; i++) {
      const msg = messages[i];
      if (msg.role === "assistant") {
        // Extract choice IDs from this assistant message
        const choiceIds = extractChoiceIds(msg.content);

        if (choiceIds.length > 0) {
          // Scan forward to find the next user message (skip intermediate messages)
          let nextUserMsg: (typeof messages)[number] | null = null;
          for (let j = i + 1; j < messages.length; j++) {
            if (messages[j].role === "user") {
              nextUserMsg = messages[j];
              break;
            }
          }

          if (nextUserMsg) {
            // Parse the assistant message to get the choice data
            const segments = parseMultipleChoice(msg.content);
            for (const segment of segments) {
              if (segment.type === "choice") {
                const choiceData = segment.content;
                // Check if the user message matches any option
                const userAnswer = nextUserMsg.content.trim();
                const matchesOption = choiceData.options.some(
                  opt => opt.label === userAnswer
                );
                // Also check for multi-select format
                const selectedLabels = userAnswer.split(",").map(s => s.trim());
                const matchesMultiple =
                  selectedLabels.length > 1 &&
                  selectedLabels.every(label =>
                    choiceData.options.some(opt => opt.label === label)
                  );

                if (
                  matchesOption ||
                  matchesMultiple ||
                  choiceData.allowCustom
                ) {
                  // Mark this choice as answered
                  answered.set(choiceData.id, userAnswer);
                }
              }
            }
          } else {
            // No user message follows - these choices are unanswered
            const segments = parseMultipleChoice(msg.content);
            const unansweredInThisMsg: Array<{
              id: string;
              question: string;
              allowMultiple: boolean;
            }> = [];
            for (const segment of segments) {
              if (segment.type === "choice") {
                unansweredInThisMsg.push({
                  id: segment.content.id,
                  question: segment.content.question,
                  allowMultiple: segment.content.allowMultiple || false,
                });
              }
            }
            if (unansweredInThisMsg.length > 0) {
              lastUnansweredMessageId = msg.id;
              lastUnansweredChoices = unansweredInThisMsg;
            }
          }
        }
      }
    }

    return { answered, lastUnansweredMessageId, lastUnansweredChoices };
  }, [messages]);

  // Initialize pending choices state when unanswered choices are detected
  useEffect(() => {
    const lastUnansweredId = answeredChoices.lastUnansweredMessageId;
    const lastUnansweredList = answeredChoices.lastUnansweredChoices;

    if (lastUnansweredId && lastUnansweredList.length > 0) {
      // Only set if not already active or if it's a different message
      if (
        !activePendingChoices ||
        activePendingChoices.messageId !== lastUnansweredId
      ) {
        setActivePendingChoices({
          messageId: lastUnansweredId,
          choices: lastUnansweredList,
          currentIndex: 0,
          collectedAnswers: new Map(),
        });
      }
    } else if (lastUnansweredId === null && activePendingChoices !== null) {
      // Clear pending choices if all have been answered
      setActivePendingChoices(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    answeredChoices.lastUnansweredMessageId,
    answeredChoices.lastUnansweredChoices,
  ]);

  // Fetch sharing status when workproductId changes
  useEffect(() => {
    const fetchSharingStatus = async () => {
      if (!workproductId) return;
      try {
        const response = await fetch(
          `${backendUrl}/api/workproduct/${workproductId}/sharing-status`,
          {
            method: "GET",
            credentials: "include",
          }
        );
        if (response.ok) {
          const data = await response.json();
          setIsPublic(data.isPublic || false);
          setShareUrl(data.shareUrl || null);
        }
      } catch (error) {
        console.error("Error fetching sharing status:", error);
      }
    };
    fetchSharingStatus();
  }, [workproductId, backendUrl]);

  // ── Overflow detection for chat controls ──────────────────────────────
  // Measures the actions row width. When too narrow for all icon buttons +
  // model switcher + submit, the icon buttons collapse into a three-dots
  // overflow menu. At even smaller widths, the model switcher hides too.
  useEffect(() => {
    const row = actionsRowRef.current;
    if (!row) return;

    // Thresholds (px): icon buttons ~132px + model ~120px + submit 32px + gaps
    const ICONS_THRESHOLD = 300;
    const MODEL_THRESHOLD = 180;

    const check = () => {
      const w = row.clientWidth;
      const hideIcons = w < ICONS_THRESHOLD;
      setChatControlsHidden(hideIcons);
      setModelSwitcherHidden(w < MODEL_THRESHOLD);
      if (!hideIcons) setShowChatOverflow(false);
    };

    const observer = new ResizeObserver(check);
    observer.observe(row);
    check();
    return () => observer.disconnect();
  }, []);

  // Close overflow popup when clicking outside
  useEffect(() => {
    if (!showChatOverflow) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        chatOverflowRef.current &&
        !chatOverflowRef.current.contains(e.target as Node)
      ) {
        setShowChatOverflow(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showChatOverflow]);

  // ============================================================================
  // CHAT ACTIONS
  // ============================================================================

  // Handle toggle sharing
  const handleToggleShare = useCallback(async () => {
    if (!workproductId || isTogglingShare) return;

    setIsTogglingShare(true);
    try {
      const response = await fetch(
        `${backendUrl}/api/workproduct/${workproductId}/toggle-sharing`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        }
      );

      if (response.ok) {
        const data = await response.json();
        setIsPublic(data.isPublic);
        setShareUrl(data.shareUrl || null);
        if (data.isPublic && data.shareUrl) {
          setShowShareModal(true);
        }
      } else {
        const errorData = await response.json();
        console.error("Error toggling share:", errorData);
      }
    } catch (error) {
      console.error("Error toggling share:", error);
    } finally {
      setIsTogglingShare(false);
    }
  }, [workproductId, isTogglingShare, backendUrl]);

  // ============================================================================
  // SEND MESSAGE
  // ============================================================================

  const handleSendMessage = useCallback(
    async (messageOverride?: string, onQueued?: () => void) => {
      const messageContent = messageOverride ?? inputValue.trim();
      if (!messageContent && selectedImages.length === 0) return;
      if (isLoading || preparingSend.current) return;
      preparingSend.current = true;
      setInitialError("");

      let activeChatId: string;
      try {
        await saveDirtyDocuments();
        activeChatId = await ensureChatExists(
          chatId,
          workproductId,
          workproductType,
          backendUrl,
          chatHooks,
          effectiveLanguage
        );
        if (!activeChatId)
          throw new Error("Could not open the conversation. Please try again.");
      } catch (e: any) {
        preparingSend.current = false;
        setInitialError(
          e.message || "Your message could not be sent. Please try again."
        );
        if (onQueued) throw e;
        return;
      }

      // Upload images to GCS first (if any), then send lightweight paths
      let uploadedImages: { gcsPath: string; proxyUrl: string }[] = [];
      if (selectedImages.length > 0) {
        try {
          uploadedImages = await Promise.all(
            selectedImages.map(async img => {
              const form = new FormData();
              form.append("file", img.file);
              const resp = await fetch(
                `${backendUrl}/api/workproduct-image-upload/${workproductId}`,
                { method: "POST", credentials: "include", body: form }
              );
              if (!resp.ok) throw new Error("Image upload failed");
              const { gcsPath, proxyUrl } = await resp.json();
              return { gcsPath, proxyUrl: `${backendUrl}${proxyUrl}` };
            })
          );
          // Notify parent about uploaded images (e.g. FTO adds them to product panel)
          if (onImagesUploaded && uploadedImages.length > 0) {
            onImagesUploaded(uploadedImages);
          }
        } catch (e) {
          console.error("Failed to upload chat images to GCS:", e);
          // Fall through — message will be sent without images
        }
      }

      // Add user message (with image URLs for display)
      const userMessage: ChatMessage = {
        id: `user_${Date.now()}`,
        role: "user",
        content: messageContent,
        timestamp: new Date(),
        ...(uploadedImages.length > 0
          ? { imageUrls: uploadedImages.map(u => u.proxyUrl) }
          : {}),
      };
      if (onNewMessage) onNewMessage(userMessage);
      onQueued?.();

      // Scroll so the new user message sits at the top of the chat window,
      // leaving maximum space for the response (ChatGPT / Gemini style).
      // Use setTimeout to ensure React has committed the new message to the DOM.
      setTimeout(() => scrollUserMessageToTop(), 50);

      // Clear input and reset streaming state
      if (!messageOverride) {
        setInputValue("");
        setSelectedImages([]);
      }
      setIsLoading(true);
      preparingSend.current = false;
      streamingMessageIdRef.current = null;
      todoMessageIdRef.current = null;
      setStreamingStarted(false);
      setThinkingContent("");
      setIsThinking(false);
      setThinkingDuration(0);
      thinkingContentRef.current = "";
      thinkingDurationRef.current = 0;
      thinkingAssistantIdRef.current = null;

      // Prepare context
      const agentContext = getContextForAgent();

      // Create abort controller
      abortControllerRef.current = new AbortController();

      try {
        // Resolve agent-specific endpoints
        const agentConfig = AGENT_CONFIGS[agentType];

        // Make streaming request
        const response = await fetch(
          `${backendUrl}${agentConfig.streamEndpoint}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            signal: abortControllerRef.current.signal,
            body: JSON.stringify({
              chatId: activeChatId,
              workproductId,
              message: userMessage.content,
              documentContext: agentContext,
              language: effectiveLanguage,
              modelProvider: betaMode ? "claude-sonnet-5" : selectedModel,
              imageGcsPaths:
                uploadedImages.length > 0
                  ? uploadedImages.map(u => u.gcsPath)
                  : undefined,
              // Agent invocation mode. Frontend mode ("ask"|"plan"|"edit")
              // maps to backend mode ("qa"|"plan"|"execute"). Edit is the
              // default; omit the field rather than send "execute" so older
              // backends keep working.
              ...(agentMode === "ask"
                ? { mode: "qa" }
                : agentMode === "plan"
                  ? { mode: "plan" }
                  : {}),
              ...(domainContextProp
                ? { domainContext: domainContextProp }
                : {}),
            }),
          }
        );

        if (!response.ok) {
          if (handleFetchUnauthorized(response)) return;
          throw new Error(`HTTP ${response.status}`);
        }

        // Process SSE stream
        const reader = response.body?.getReader();
        if (!reader) throw new Error("No response body");

        const decoder = new TextDecoder();

        // Create SSE processor
        const sseCallbacks: SSEProcessorTipTapCallbacks = {
          onMessage: message => {
            if (onNewMessage) onNewMessage(message);
          },
          onError: errorMsg => {
            console.error("Agent error:", errorMsg);
          },
          onContextUsage: usage => {
            setContextUsage(usage);
          },
          // Thinking/reasoning callbacks
          onThinkingStart: () => {
            setIsThinking(true);
            setStreamingStarted(true);
          },
          onThinkingChunk: chunk => {
            thinkingContentRef.current += chunk;
            setThinkingContent(thinkingContentRef.current);
            debouncedScrollCheck();
          },
          onThinkingComplete: durationSec => {
            setIsThinking(false);
            setThinkingDuration(durationSec);
            thinkingDurationRef.current = durationSec;
          },
          onTextChunk: chunk => {
            setStreamingStarted(true);

            // Follow the same pattern as DocumentAgentChat:
            // Create a new message on first chunk, then update it with subsequent chunks
            if (!streamingMessageIdRef.current) {
              // First chunk - create new assistant message
              const msgId = `assistant_${Date.now()}`;
              streamingMessageIdRef.current = msgId;
              // Link thinking block to this assistant message
              if (thinkingContentRef.current) {
                thinkingAssistantIdRef.current = msgId;
              }
              const newMsg: ChatMessage = {
                id: msgId,
                role: "assistant",
                content: chunk,
                timestamp: new Date(),
              };
              if (onNewMessage) onNewMessage(newMsg);
            } else {
              // Subsequent chunks - update existing message using update_ prefix
              const updateMsg: ChatMessage = {
                id: `update_${streamingMessageIdRef.current}_${Date.now()}`,
                role: "assistant",
                content: chunk,
                timestamp: new Date(),
              };
              if (onNewMessage) onNewMessage(updateMsg);
            }

            // Don't auto-scroll — let the user message stay at the top.
            // Just update the scroll-to-bottom button visibility.
            debouncedScrollCheck();
          },
          onScrollCheck: debouncedScrollCheck,
          onToolCall: data => {
            // Reset streaming anchor so that any text arriving after this
            // tool call starts a NEW assistant message (displayed below the
            // tool messages) instead of being appended to the pre-tool
            // assistant message.
            streamingMessageIdRef.current = null;

            // For ask_user_choice: skip the tool spinner entirely.
            // The synthetic assistant message is created in onToolResult.
            if (data.toolName === "ask_user_choice") {
              return;
            }

            // For write_todos: reuse the existing todo widget instead of
            // creating a new loading message for every invocation.
            if (data.toolName === "write_todos" && todoMessageIdRef.current) {
              // A todo widget already exists in the chat — skip the spinner.
              // onToolResult will update the same widget with the new data.
              return;
            }

            // Use a deterministic ID based on callId so onToolResult can
            // update this message in-place via the meta_ prefix convention.
            const toolMsgId = data.callId
              ? `tool_${data.callId}`
              : `tool_call_${Date.now()}`;

            // Remember the first write_todos message ID for later reuse
            if (data.toolName === "write_todos") {
              todoMessageIdRef.current = toolMsgId;
            }

            const toolMsg: ChatMessage = {
              id: toolMsgId,
              role: "tool",
              content: JSON.stringify({ action: "tool_call", ...data }),
              toolName: data.toolName,
              timestamp: new Date(),
            };
            if (onNewMessage) onNewMessage(toolMsg);
          },
          onToolResult: data => {
            // Show concise summaries for read-only tools instead of full content
            let messageContent: string;
            const output = (data.toolOutput || {}) as Record<string, any>;

            if (data.toolName === "view_document") {
              const docName = output.documentName || "document";
              const docType = output.documentType || "tiptap";
              const nodeCount = output.nodeCount || 0;
              const commentCount = output.commentCount || 0;
              const trackChangeCount = output.trackChangeCount || 0;

              let summary: string;
              if (docType === "pdf") {
                const pageCount = output.pageCount || 0;
                const imageCount = output.imageCount || 0;
                summary = `Viewed "${docName}" (${pageCount} pages)`;
                if (imageCount > 0) {
                  summary += `, ${imageCount} image(s) available`;
                }
              } else {
                summary = `Viewed "${docName}" (${nodeCount} sections)`;
                if (commentCount > 0 || trackChangeCount > 0) {
                  const extras = [];
                  if (commentCount > 0) extras.push(`${commentCount} comments`);
                  if (trackChangeCount > 0)
                    extras.push(`${trackChangeCount} pending changes`);
                  summary += ` - ${extras.join(", ")}`;
                }
              }

              messageContent = JSON.stringify({
                action: "tool_result",
                toolName: data.toolName,
                message: summary,
              });
            } else if (data.toolName === "view_image") {
              const imageCount = output.imageCount || 0;
              messageContent = JSON.stringify({
                action: "tool_result",
                toolName: data.toolName,
                message: `Loaded ${imageCount} image(s)`,
              });

              // Extract image URLs for inline display (proxy paths or signed URLs)
              const imageUrls: string[] = Array.isArray(output.images)
                ? output.images
                    .map((img: any) => {
                      const raw = String(img?.signedUrl || "").trim();
                      if (raw.startsWith("/api/image-proxy/"))
                        return `${backendUrl}${raw}`;
                      if (/^https?:\/\//i.test(raw)) return raw;
                      return "";
                    })
                    .filter((u: string) => u.length > 0)
                : [];

              if (imageUrls.length > 0) {
                // Update the loading message in-place if callId is available
                if (data.callId && onNewMessage) {
                  onNewMessage({
                    id: `meta_tool_${data.callId}`,
                    content: JSON.stringify({
                      action: "tool_result",
                      toolName: data.toolName,
                      message: `Loaded ${imageUrls.length} image(s)`,
                    }),
                    imageUrls: imageUrls.slice(0, 10),
                  } as any);
                  return;
                }

                // Fallback: create a separate message to display the images
                const imageMsg: ChatMessage = {
                  id: `view_image_display_${Date.now()}`,
                  role: "tool",
                  content: JSON.stringify({
                    action: "tool_result",
                    toolName: data.toolName,
                    message: `Loaded ${imageUrls.length} image(s)`,
                  }),
                  toolName: data.toolName,
                  timestamp: new Date(),
                  imageUrls: imageUrls.slice(0, 10),
                };
                if (onNewMessage) onNewMessage(imageMsg);
                return; // Skip the default tool message creation below
              }
            } else if (data.toolName === "create_image") {
              const width = output.width || "?";
              const height = output.height || "?";
              messageContent = JSON.stringify({
                action: "tool_result",
                toolName: data.toolName,
                message: `Generated image (${width}x${height})`,
              });

              // Extract the single image URL for inline display (proxy path or signed URL)
              let imageUrl = output.imageUrl
                ? String(output.imageUrl).trim()
                : "";
              if (imageUrl.startsWith("/api/image-proxy/")) {
                imageUrl = `${backendUrl}${imageUrl}`;
              }

              if (imageUrl && /^https?:\/\//i.test(imageUrl)) {
                // Update the loading message in-place if callId is available
                if (data.callId && onNewMessage) {
                  onNewMessage({
                    id: `meta_tool_${data.callId}`,
                    content: JSON.stringify({
                      action: "tool_result",
                      toolName: data.toolName,
                      message: `Generated image (${width}x${height})`,
                    }),
                    imageUrls: [imageUrl],
                  } as any);
                  return;
                }

                // Fallback: create a separate message to display the image
                const imageMsg: ChatMessage = {
                  id: `create_image_display_${Date.now()}`,
                  role: "tool",
                  content: JSON.stringify({
                    action: "tool_result",
                    toolName: data.toolName,
                    message: `Generated image (${width}x${height})`,
                  }),
                  toolName: data.toolName,
                  timestamp: new Date(),
                  imageUrls: [imageUrl],
                };
                if (onNewMessage) onNewMessage(imageMsg);
                return; // Skip the default tool message creation below
              }
            } else if (data.toolName === "list_documents") {
              const total = output.totalDocuments || 0;
              messageContent = JSON.stringify({
                action: "tool_result",
                toolName: data.toolName,
                message: `Listed ${total} document(s)`,
              });
            } else if (data.toolName === "search_documents") {
              const totalMatches = output.totalMatches || 0;
              const docsWithMatches = output.documentsWithMatches || 0;
              const query = output.query || "";
              messageContent = JSON.stringify({
                action: "tool_result",
                toolName: data.toolName,
                message: `Search "${query}": ${totalMatches} match(es) across ${docsWithMatches} document(s)`,
              });
            } else if (data.toolName === "get_document_outline") {
              const docName = output.documentName || "document";
              const headingCount = output.headingCount || 0;
              messageContent = JSON.stringify({
                action: "tool_result",
                toolName: data.toolName,
                message: `Outline of "${docName}": ${headingCount} heading(s)`,
              });
            } else if (data.toolName === "read_section") {
              const docName = output.documentName || "document";
              const returnedNodes = output.returnedNodes || 0;
              const sectionTitle = output.sectionTitle || "";
              messageContent = JSON.stringify({
                action: "tool_result",
                toolName: data.toolName,
                message: `Read section "${sectionTitle}" from "${docName}": ${returnedNodes} node(s)`,
              });
            } else if (data.toolName === "create_document") {
              const createdDocs = output.createdDocuments || [];
              const docName =
                createdDocs.length > 0
                  ? createdDocs[0].documentName
                  : "document";
              messageContent = JSON.stringify({
                action: "tool_result",
                toolName: data.toolName,
                message: `Created document "${docName}"`,
              });
            } else if (data.toolName === "find_and_replace") {
              const matchCount = output.matchCount || 0;
              const isPreview = output.preview === true;
              messageContent = JSON.stringify({
                action: "tool_result",
                toolName: data.toolName,
                message: isPreview
                  ? `Preview: ${matchCount} match(es) found`
                  : `Replaced ${matchCount} occurrence(s)`,
              });
            } else if (data.toolName === "manage_track_changes") {
              messageContent = JSON.stringify({
                action: "tool_result",
                toolName: data.toolName,
                message: output.message || "Track changes updated",
              });
            } else if (data.toolName === "manage_comments") {
              const action = output.action || "";
              if (action === "list" || output.totalComments !== undefined) {
                const total = output.totalComments || 0;
                messageContent = JSON.stringify({
                  action: "tool_result",
                  toolName: data.toolName,
                  message: `Listed ${total} comment(s)`,
                });
              } else {
                messageContent = JSON.stringify({
                  action: "tool_result",
                  toolName: data.toolName,
                  message: output.message || "Comment updated",
                });
              }
            } else if (data.toolName === "update_document") {
              messageContent = JSON.stringify({
                action: "tool_result",
                toolName: data.toolName,
                message: output.message || "Document updated",
              });
            } else if (data.toolName === "write_todos") {
              const todos = Array.isArray(output.todos) ? output.todos : [];
              const pending = todos.filter(
                (t: any) => t.status === "pending"
              ).length;
              const inProgress = todos.filter(
                (t: any) => t.status === "in_progress"
              ).length;
              const completed = todos.filter(
                (t: any) => t.status === "completed"
              ).length;
              const summary = `Task plan: ${todos.length} task(s) — ${completed} done, ${inProgress} in progress, ${pending} pending`;
              const todoContent = JSON.stringify({
                action: "tool_result",
                toolName: data.toolName,
                message: summary,
                todos,
              });

              // Always update the single todo widget tracked by todoMessageIdRef.
              // This collapses multiple write_todos calls into one live-updating widget.
              const targetId = todoMessageIdRef.current;
              if (targetId && onNewMessage) {
                onNewMessage({
                  id: `meta_${targetId}`,
                  content: todoContent,
                } as any);
              } else if (onNewMessage) {
                // Fallback: no tracked widget (shouldn't happen), create one
                const fallbackId = `todo_${Date.now()}`;
                todoMessageIdRef.current = fallbackId;
                onNewMessage({
                  id: fallbackId,
                  role: "tool",
                  content: todoContent,
                  toolName: data.toolName,
                  timestamp: new Date(),
                } as any);
              }
              return; // Skip the generic update logic below
            } else if (data.toolName === "ask_user_choice") {
              // Construct <multiple-choice> XML from the tool output,
              // creating a synthetic assistant message that reuses 100% of
              // the existing MultipleChoiceUI rendering infrastructure.
              const choiceData = {
                id: output.choiceId || `mc_${Date.now()}`,
                question: output.question || "",
                options: output.options || [],
                allowMultiple: output.allowMultiple ?? false,
                allowCustom: output.allowCustom ?? true,
                customPlaceholder:
                  output.customPlaceholder || "Or type your own answer...",
              };

              const xmlContent = `<multiple-choice>\n${JSON.stringify(choiceData, null, 2)}\n</multiple-choice>`;

              if (onNewMessage) {
                onNewMessage({
                  id: `assistant_choice_${choiceData.id}_${Date.now()}`,
                  role: "assistant",
                  content: xmlContent,
                  timestamp: new Date(),
                });
              }

              // Reset streaming anchor so the LLM's post-tool text response
              // starts a NEW assistant message.
              streamingMessageIdRef.current = null;
              return; // Skip the generic update logic below
            } else if (data.toolName === "load_skill") {
              // Parse the structured prefix from skillLoader.ts:
              // "[Loaded skill: Skill Name (skill_id)]"
              // Note: parseToolOutput wraps plain strings as { message: "..." }
              const skillText =
                typeof data.toolOutput === "string"
                  ? data.toolOutput
                  : (data.toolOutput as any)?.message || "";
              const prefixMatch = skillText.match(
                /^\[Loaded skill:\s*(.+?)\s*\(([^)]+)\)\]/
              );
              let displayMessage: string;
              if (prefixMatch) {
                const [, skillName, skillId] = prefixMatch;
                displayMessage = `Loaded skill: ${skillName} (${skillId})`;
              } else {
                // Fallback: extract first heading from content
                const headingMatch = skillText.match(/^#+\s+(.+?)(?:\n|$)/m);
                displayMessage = headingMatch
                  ? `Loaded skill: ${headingMatch[1].trim()}`
                  : "Loaded skill";
              }
              messageContent = JSON.stringify({
                action: "tool_result",
                toolName: data.toolName,
                message: displayMessage,
              });
            } else {
              messageContent = JSON.stringify({
                action: "tool_result",
                ...data,
              });
            }

            // Update the loading message in-place via meta_ if callId is
            // available; otherwise fall back to creating a separate message.
            if (data.callId && onNewMessage) {
              onNewMessage({
                id: `meta_tool_${data.callId}`,
                content: messageContent,
              } as any);
            } else {
              const toolMsg: ChatMessage = {
                id: `tool_result_${Date.now()}`,
                role: "tool",
                content: messageContent,
                toolName: data.toolName,
                timestamp: new Date(),
              };
              if (onNewMessage) onNewMessage(toolMsg);
            }
            // Note: patch is applied by SSE processor's handleDocumentUpdate, not here
            // to avoid duplicate application
          },
          applyPatch,
          addTipTapDocument: (docId, documentName, content, editable) => {
            addTipTapDocument(docId, documentName, content, editable);
          },
          addPDFDocument,
          addFormDocument,
          updateFormData,
          getDocumentById,
          saveDirtyDocuments,
          onDocumentActivated: (docId, documentName) => {
            if (onDocumentActivated) onDocumentActivated(docId, documentName);
            if (setActiveRightTab) setActiveRightTab(docId);
          },
          translate: (key: string, params?: Record<string, any>) =>
            t(key, params) as string,
          shouldHandleTool: updateStrategy.shouldHandleTool,
          getSessionId: () => activeChatId,
          patchesEndpoint: agentConfig.patchesEndpoint,
          workproductId,
        };

        // Determine initialPatchSeq. On a fresh page session, seed from the
        // server's latest seq so we don't replay patches already baked into the
        // saved document. Within a session we resume from the in-memory value —
        // BUT the 24h patch-cleanup cron can delete a session's rows and restart
        // its sequence at 1, and the cached (higher) value would then silently
        // skip every new patch. So always re-check the server: if its latest seq
        // is BELOW our cached value, the sequence restarted → reset to 0.
        let initialPatchSeq = patchSeqByChatRef.current[activeChatId];
        let serverLatestSeq: number | undefined;
        try {
          const seqRes = await fetch(
            `${backendUrl}/api/document-patches/latest-seq?sessionId=${encodeURIComponent(activeChatId)}${workproductId ? `&workproductId=${encodeURIComponent(workproductId)}` : ""}`,
            { credentials: "include" }
          );
          if (seqRes.ok) {
            const seqData = await seqRes.json();
            if (typeof seqData.latestSeq === "number") {
              serverLatestSeq = seqData.latestSeq;
            }
          }
        } catch {
          // Network error — fall back to the cached value (or 0) below.
        }

        if (initialPatchSeq === undefined) {
          initialPatchSeq = serverLatestSeq ?? 0;
        } else if (
          serverLatestSeq !== undefined &&
          serverLatestSeq < initialPatchSeq
        ) {
          // Sequence restarted after a cron cleanup — don't skip the new patches.
          initialPatchSeq = 0;
        }
        patchSeqByChatRef.current[activeChatId] = initialPatchSeq;

        const sseProcessor = createSSEProcessorTipTap(sseCallbacks, {
          initialPatchSeq,
        });

        // Mark agent as working — pauses auto-save and locks editors
        setAgentWorking(true);

        // Start fallback polling for persist-first patches.
        // This catches any SSE nudges that App Engine might drop.
        const PATCH_POLL_INTERVAL_MS = 3000;
        const patchPollTimer = setInterval(() => {
          if (activeChatId) {
            sseProcessor.fetchAndApplyPatches(activeChatId);
          }
        }, PATCH_POLL_INTERVAL_MS);

        // Read stream
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            const text = decoder.decode(value, { stream: true });
            sseProcessor.processChunk(text);
          }
        } finally {
          // Stop polling and do one final fetch to catch any stragglers
          clearInterval(patchPollTimer);
          if (activeChatId) {
            try {
              await sseProcessor.fetchAndApplyPatches(activeChatId);
            } catch (e) {}
            // Persist the last applied seq so the next message in this chat
            // doesn't replay already-applied patches.
            patchSeqByChatRef.current[activeChatId] =
              sseProcessor.getLastAppliedSeq();
          }

          // Round 3: after a plan-mode turn, fetch the pending proposals so
          // the user can review/approve/reject them.
          if (agentMode === "plan" && activeChatId) {
            try {
              const resp = await fetch(
                `${backendUrl}/api/agent/proposals?threadId=${encodeURIComponent(activeChatId)}&workproductId=${encodeURIComponent(workproductId)}`,
                { credentials: "include" }
              );
              if (resp.ok) {
                const data = await resp.json();
                setPendingProposals(
                  Array.isArray(data?.proposals)
                    ? data.proposals.map((p: any) => ({
                        id: p.id,
                        toolName: p.toolName ?? p.tool_name,
                        args: p.args,
                        rationale: p.rationale,
                      }))
                    : []
                );
              }
            } catch (e) {
              // non-fatal
            }
          }
        }

        // No need to add final assistant message - content is already in the messages
        // via the onTextChunk updates
      } catch (error: any) {
        if (error.name !== "AbortError") {
          console.error("Chat error:", error);
          const errorMessage: ChatMessage = {
            id: `error_${Date.now()}`,
            role: "system",
            content: `Error: ${error.message || "Failed to get response"}`,
            timestamp: new Date(),
          };
          if (onNewMessage) onNewMessage(errorMessage);
        }
      } finally {
        setIsLoading(false);
        // Mark agent as done — unlocks editors and triggers final save
        setAgentWorking(false);
        // Persist thinking data into the assistant message so it survives refresh
        if (
          thinkingContentRef.current &&
          thinkingAssistantIdRef.current &&
          onNewMessage
        ) {
          // Send metadata-only update to persist thinking data on the assistant message.
          // Uses the meta_ prefix convention recognized by handleNewMessage.
          onNewMessage({
            id: `meta_${thinkingAssistantIdRef.current}`,
            thinkingContent: thinkingContentRef.current,
            thinkingDuration: thinkingDurationRef.current || undefined,
          } as any);
        }
        streamingMessageIdRef.current = null;
        abortControllerRef.current = null;
        // If thinking was still in progress when stream ended, mark it complete
        if (thinkingContentRef.current) {
          setIsThinking(false);
        }
      }
    },
    [
      inputValue,
      selectedImages,
      isLoading,
      chatId,
      workproductId,
      backendUrl,
      chatHooks,
      effectiveLanguage,
      onNewMessage,
      getContextForAgent,
      saveDirtyDocuments,
      applyPatch,
      addTipTapDocument,
      addPDFDocument,
      addFormDocument,
      updateFormData,
      getDocumentById,
      onDocumentActivated,
      setActiveRightTab,
      setAgentWorking,
      t,
      updateStrategy,
      debouncedScrollCheck,
      scrollUserMessageToTop,
      selectedModel,
      betaMode,
      agentType,
      workproductType,
    ]
  );

  useEffect(() => {
    if (
      !initialMessage ||
      initialSent.current ||
      isConversationLoading ||
      readOnly ||
      inputDisabled
    )
      return;
    initialSent.current = true;
    // A refreshed route may retain navigation state after the first message was saved.
    if (hasUserMessages) {
      onInitialMessageConsumed?.();
      return;
    }
    const consume = () => {
      if (mounted.current) onInitialMessageConsumed?.();
    };
    void handleSendMessage(initialMessage, consume).catch(e => {
      if (!mounted.current) return;
      setInputValue(initialMessage);
      setInitialError(
        e.message || "Your message could not be sent. Please try again."
      );
      consume();
    });
  }, [
    initialMessage,
    isConversationLoading,
    readOnly,
    inputDisabled,
    hasUserMessages,
    handleSendMessage,
    onInitialMessageConsumed,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey && !isComposing) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage, isComposing]
  );

  const handleStopGenerating = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  // Handle multiple choice selection with sequential reveal and batching
  const handleChoiceSelect = useCallback(
    (choiceId: string, selectedLabels: string[]) => {
      if (selectedLabels.length === 0 || !activePendingChoices) return;

      // Add this answer to collected answers
      const newCollectedAnswers = new Map(
        activePendingChoices.collectedAnswers
      );
      newCollectedAnswers.set(choiceId, selectedLabels);

      // Move to next question or submit all
      const nextIndex = activePendingChoices.currentIndex + 1;

      if (nextIndex < activePendingChoices.choices.length) {
        // Show next question
        setActivePendingChoices({
          ...activePendingChoices,
          currentIndex: nextIndex,
          collectedAnswers: newCollectedAnswers,
        });
      } else {
        // All questions answered - format and send batch message
        const batchedAnswer = activePendingChoices.choices
          .map(choice => {
            const labels = newCollectedAnswers.get(choice.id) || [];
            return `${choice.question}\nAnswer: ${labels.join(", ")}`;
          })
          .join("\n\n");

        // Clear pending choices state
        setActivePendingChoices(null);

        // Send the batched answer as a message
        handleSendMessage(batchedAnswer);
      }
    },
    [activePendingChoices, handleSendMessage]
  );

  // ============================================================================
  // IMAGE HANDLING
  // ============================================================================

  const handlePickImages = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files: File[] = Array.from(e.target.files || []);
    if (!files.length) return;
    const max = 5 - selectedImages.length;
    if (max <= 0) {
      alert(t("documentAgentChat.maxImagesPerMessage"));
      return;
    }
    const accepted: File[] = files
      .filter((f: File) => /^image\/(png|jpe?g|webp)$/i.test(f.type))
      .filter((f: File) => f.size <= 10 * 1024 * 1024)
      .slice(0, Math.max(0, max));
    const mapped = accepted.map((file: File) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setSelectedImages(prev => [...prev, ...mapped]);
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const handleRemoveImage = (idx: number) => {
    setSelectedImages(prev => {
      const copy = [...prev];
      URL.revokeObjectURL(copy[idx].previewUrl);
      copy.splice(idx, 1);
      return copy;
    });
  };

  // ============================================================================
  // DOCUMENT IMPORT (DOCX/PDF)
  // ============================================================================

  const handleImportDocx = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !onImportDocxAsNewDocument) return;

    if (
      file.name.toLowerCase().endsWith(".doc") &&
      !file.name.toLowerCase().endsWith(".docx")
    ) {
      alert(
        `"${file.name}" is a legacy .doc file and is not supported. Please save it as .docx and try again.`
      );
      if (docxInputRef.current) docxInputRef.current.value = "";
      return;
    }

    try {
      setIsUploading(true);
      await onImportDocxAsNewDocument(file);
    } catch (error) {
      console.error("Error importing DOCX:", error);
      alert(t("documentAgentChat.failedImportDocx"));
    } finally {
      setIsUploading(false);
      if (docxInputRef.current) docxInputRef.current.value = "";
    }
  };

  const handleImportPdf = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !onImportPdfAsNewDocument) return;

    try {
      setIsUploading(true);
      await onImportPdfAsNewDocument(file);
    } catch (error) {
      console.error("Error importing PDF:", error);
      alert(t("documentAgentChat.failedImportPdf"));
    } finally {
      setIsUploading(false);
      if (pdfInputRef.current) pdfInputRef.current.value = "";
    }
  };

  const handleDocxButtonClick = () => {
    setShowUploadDropdown(false);
    docxInputRef.current?.click();
  };

  const handlePdfButtonClick = () => {
    setShowUploadDropdown(false);
    pdfInputRef.current?.click();
  };

  // Handle textarea change
  const handleTextareaChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setInputValue(e.target.value);
      setTextareaRows(4);
    },
    []
  );

  // Form submit handler
  const handleSubmitStream = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (inputDisabled || (!inputValue.trim() && selectedImages.length === 0))
        return;
      await handleSendMessage();
    },
    [inputDisabled, inputValue, selectedImages.length, handleSendMessage]
  );

  // ============================================================================
  // RENDER HELPERS (for overflow-aware chat controls)
  // ============================================================================

  /** Upload dropdown (Word / PDF / Image) */
  const renderUploadDropdown = () => (
    <Dropdown
      show={showUploadDropdown}
      onToggle={isOpen => setShowUploadDropdown(isOpen)}
    >
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id="upload-tooltip">
            {t("documentAgentChat.uploadFiles")}
          </Tooltip>
        }
      >
        <Dropdown.Toggle
          as={Button}
          variant="outline-secondary"
          size="sm"
          className="patent-draft-agent-file-upload-btn"
          disabled={inputDisabled || isLoading}
          bsPrefix="btn-no-caret"
          style={{
            border: "1px solid #ced4da",
            borderRadius: "4px",
            padding: "4px 8px",
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            fill="currentColor"
            viewBox="0 0 16 16"
          >
            <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
            <path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708l3-3z" />
          </svg>
          <span className="ms-1">{t("documentAgentChat.attach")}</span>
        </Dropdown.Toggle>
      </OverlayTrigger>
      <Dropdown.Menu>
        <Dropdown.Item
          onClick={handleDocxButtonClick}
          disabled={!onImportDocxAsNewDocument || isUploading}
          style={{
            display: "flex",
            alignItems: "center",
            whiteSpace: "nowrap",
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            fill="currentColor"
            viewBox="0 0 16 16"
            style={{ marginRight: 8, flexShrink: 0 }}
          >
            <path d="M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5L14 4.5zm-3 0A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4.5h-2z" />
          </svg>
          {t("documentAgentChat.wordDocument")}
        </Dropdown.Item>
        <Dropdown.Item
          onClick={handlePdfButtonClick}
          disabled={!onImportPdfAsNewDocument || isUploading}
          style={{
            display: "flex",
            alignItems: "center",
            whiteSpace: "nowrap",
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            fill="currentColor"
            viewBox="0 0 16 16"
            style={{ marginRight: 8, flexShrink: 0 }}
          >
            <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2zM9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5v2z" />
            <path d="M4.603 14.087a.81.81 0 0 1-.438-.42c-.195-.388-.13-.776.08-1.102.198-.307.526-.568.897-.787a7.68 7.68 0 0 1 1.482-.645 19.697 19.697 0 0 0 1.062-2.227 7.269 7.269 0 0 1-.43-1.295c-.086-.4-.119-.796-.046-1.136.075-.354.274-.672.65-.823.192-.077.4-.12.602-.077a.7.7 0 0 1 .477.365c.088.164.12.356.127.538.007.188-.012.396-.047.614-.084.51-.27 1.134-.52 1.794a10.954 10.954 0 0 0 .98 1.686 5.753 5.753 0 0 1 1.334.05c.364.066.734.195.96.465.12.144.193.32.2.518.007.192-.047.382-.138.563a1.04 1.04 0 0 1-.354.416.856.856 0 0 1-.51.138c-.331-.014-.654-.196-.933-.417a5.712 5.712 0 0 1-.911-.95 11.651 11.651 0 0 0-1.997.406 11.307 11.307 0 0 1-1.02 1.51c-.292.35-.609.656-.927.787a.793.793 0 0 1-.58.029zm1.379-1.901c-.166.076-.32.156-.459.238-.328.194-.541.383-.647.547-.094.145-.096.25-.04.361.01.022.02.036.026.044a.266.266 0 0 0 .035-.012c.137-.056.355-.235.635-.572a8.18 8.18 0 0 0 .45-.606zm1.64-1.33a12.71 12.71 0 0 1 1.01-.193 11.744 11.744 0 0 1-.51-.858 20.801 20.801 0 0 1-.5 1.05zm2.446.45c.15.163.296.3.435.41.24.19.407.253.498.256a.107.107 0 0 0 .07-.015.307.307 0 0 0 .094-.125.436.436 0 0 0 .059-.2.095.095 0 0 0-.026-.063c-.052-.062-.2-.152-.518-.209a3.876 3.876 0 0 0-.612-.053zM8.078 7.8a6.7 6.7 0 0 0 .2-.828c.031-.188.043-.343.038-.465a.613.613 0 0 0-.032-.198.517.517 0 0 0-.145.04c-.087.035-.158.106-.196.283-.04.192-.03.469.046.822.024.111.054.227.09.346z" />
          </svg>
          {t("documentAgentChat.pdfDocument")}
        </Dropdown.Item>
        <Dropdown.Item
          onClick={() => {
            imageInputRef.current?.click();
            setShowUploadDropdown(false);
          }}
          style={{
            display: "flex",
            alignItems: "center",
            whiteSpace: "nowrap",
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            fill="currentColor"
            viewBox="0 0 16 16"
            style={{ marginRight: 8, flexShrink: 0 }}
          >
            <path d="M6.002 5.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z" />
            <path d="M2.002 1a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2h-12zm12 1a1 1 0 0 1 1 1v6.5l-3.777-1.947a.5.5 0 0 0-.577.093l-3.71 3.71-2.66-1.772a.5.5 0 0 0-.63.062L1.002 12V3a1 1 0 0 1 1-1h12z" />
          </svg>
          {t("documentAgentChat.image")}
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );

  /** Share button */
  const renderShareButton = () =>
    minimalChrome || hideShareButton || !publicConfig?.beta?.publicSharing ? null : (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id="share-tooltip">
            {isPublic
              ? t("documentAgentChat.makePrivate")
              : t("documentAgentChat.shareReport")}
          </Tooltip>
        }
      >
        <Button
          variant={isPublic ? "success" : "outline-secondary"}
          size="sm"
          onClick={handleToggleShare}
          className="patent-draft-agent-share-btn"
          disabled={!workproductId || isTogglingShare || inputDisabled}
        >
          {isTogglingShare ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path d="M11 2.5a2.5 2.5 0 1 1 .603 1.628l-6.718 3.12a2.499 2.499 0 0 1 0 1.504l6.718 3.12a2.5 2.5 0 1 1-.488.876l-6.718-3.12a2.5 2.5 0 1 1 0-3.256l6.718-3.12A2.5 2.5 0 0 1 11 2.5z" />
            </svg>
          )}
        </Button>
      </OverlayTrigger>
    );

  /** Model selector dropdown */
  const renderModelSelector = () => betaMode ? null : (
    <Dropdown
      show={showModelDropdown}
      onToggle={isOpen => setShowModelDropdown(isOpen)}
    >
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id="model-selector-tooltip">
            {t("documentAgentChat.selectModel", "Select AI Model")}
          </Tooltip>
        }
      >
        <Dropdown.Toggle
          variant="link"
          size="sm"
          className="patent-draft-agent-model-selector-btn"
          disabled={inputDisabled || isLoading}
          style={{
            fontSize: "12px",
            fontWeight: 500,
            color: "#6c757d",
            textDecoration: "none",
            padding: "4px 8px",
          }}
        >
          {MODEL_DISPLAY_NAMES[selectedModel]}
        </Dropdown.Toggle>
      </OverlayTrigger>
      <Dropdown.Menu
        style={{
          minWidth: "160px",
          padding: "8px 0",
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          border: "1px solid #e9ecef",
          borderRadius: "8px",
        }}
      >
        {MODEL_ORDER.map(modelKey => (
          <OverlayTrigger
            key={modelKey}
            placement="right"
            overlay={
              <Tooltip id={`model-desc-${modelKey}`}>
                {MODEL_DESCRIPTIONS[modelKey]}
                <div style={{ marginTop: 4, fontSize: "11px", opacity: 0.85 }}>
                  {t("models.costCoefficient", {
                    value: MODEL_COST_COEFFICIENTS[modelKey],
                  })}
                </div>
              </Tooltip>
            }
          >
            <Dropdown.Item
              onClick={() => handleModelSelect(modelKey)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 16px",
                fontSize: "13px",
              }}
            >
              <span>{MODEL_DISPLAY_NAMES[modelKey]}</span>
              {selectedModel === modelKey && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" />
                </svg>
              )}
            </Dropdown.Item>
          </OverlayTrigger>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );

  /** Agent mode dropdown — low-profile, sits left of Send. */
  const AGENT_MODE_LABEL: Record<"ask" | "plan" | "edit", string> = {
    ask: t("documentAgentChat.agentModeAsk", "Ask"),
    plan: t("documentAgentChat.agentModePlan", "Plan"),
    edit: t("documentAgentChat.agentModeEdit", "Execute"),
  };
  const AGENT_MODE_TOOLTIP: Record<"ask" | "plan" | "edit", string> = {
    ask: t(
      "documentAgentChat.agentModeAskTooltip",
      "Ask: read-only. The agent can search and view documents and answer questions, but cannot edit them."
    ),
    plan: t(
      "documentAgentChat.agentModePlanTooltip",
      "Plan: the agent proposes changes for your review before applying anything."
    ),
    edit: t(
      "documentAgentChat.agentModeEditTooltip",
      "Execute: the agent can edit documents and take actions directly."
    ),
  };
  const renderAgentModeSelector = () =>
    minimalChrome || hideModeSelector || consumerMode ? null : (
      <Dropdown
        show={showAgentModeDropdown}
        onToggle={isOpen => setShowAgentModeDropdown(isOpen)}
      >
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id="agent-mode-selector-tooltip">
              {AGENT_MODE_TOOLTIP[agentMode]}
            </Tooltip>
          }
        >
          <Dropdown.Toggle
            variant="link"
            size="sm"
            className="patent-draft-agent-mode-selector-btn"
            disabled={isLoading}
            style={{
              fontSize: "12px",
              fontWeight: 500,
              color: "#6c757d",
              textDecoration: "none",
              padding: "4px 8px",
            }}
          >
            {AGENT_MODE_LABEL[agentMode]}
          </Dropdown.Toggle>
        </OverlayTrigger>
        <Dropdown.Menu
          style={{
            minWidth: "200px",
            padding: "8px 0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            border: "1px solid #e9ecef",
            borderRadius: "8px",
          }}
        >
          {(["ask", "plan", "edit"] as const).map(modeOption => (
            <OverlayTrigger
              key={modeOption}
              placement="right"
              overlay={
                <Tooltip id={`agent-mode-desc-${modeOption}`}>
                  {AGENT_MODE_TOOLTIP[modeOption]}
                </Tooltip>
              }
            >
              <Dropdown.Item
                onClick={() => {
                  setAgentMode(modeOption);
                  setShowAgentModeDropdown(false);
                }}
                className={`patent-draft-agent-mode-${modeOption}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 16px",
                  fontSize: "13px",
                }}
              >
                <span>{AGENT_MODE_LABEL[modeOption]}</span>
                {agentMode === modeOption && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" />
                  </svg>
                )}
              </Dropdown.Item>
            </OverlayTrigger>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    );

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="patent-draft-agent-chat-window">
      <div className="patent-draft-agent-chat-body">
        {!workproductId && (
          <div
            style={{
              padding: "8px",
              backgroundColor: workproductId ? "#d4edda" : "#f8d7da",
              borderBottom: "1px solid #ccc",
              fontSize: "12px",
              textAlign: "center",
            }}
          >
            {t("documentAgentChat.waitingForWorkproduct")}
          </div>
        )}

        {/* Message history */}
        <div
          className="patent-draft-agent-message-history"
          ref={messageHistoryRef}
          onScroll={handleScroll}
        >
          {isConversationLoading ? (
            <div
              role="status"
              aria-live="polite"
              style={{
                minHeight: "220px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "12px",
                color: "#5f6b76",
              }}
            >
              <Spinner animation="border" size="sm" />
              <span>{t("documentAgentChat.loadingConversation")}</span>
            </div>
          ) : messages.length === 0 ? (
            welcomeMessageOverride ? (
              <div className="patent-draft-agent-welcome-message">
                {welcomeMessageOverride}
              </div>
            ) : (
              <WelcomeMessage />
            )
          ) : (
            (() => {
              const lastAssistantIndex = (() => {
                for (let i = messages.length - 1; i >= 0; i--) {
                  if (messages[i]?.role === "assistant") return i;
                }
                return -1;
              })();

              const thinkingBlockElement =
                isThinking || (thinkingContent && !hideReasoning) ? (
                  <div
                    key="thinking-block"
                    className="patent-draft-agent-message assistant-message"
                  >
                    <div className="patent-draft-agent-message-container">
                      <div className="patent-draft-agent-text">
                        <ThinkingBlock
                          thinkingContent={thinkingContent}
                          isThinking={isThinking}
                          durationSeconds={thinkingDuration}
                        />
                      </div>
                    </div>
                  </div>
                ) : null;

              // Determine if the last assistant message is the one the thinking
              // block belongs to. Uses a persistent ref that survives after
              // streaming ends (unlike streamingMessageIdRef which resets).
              const thinkingBelongsToLastAssistant =
                lastAssistantIndex >= 0 &&
                thinkingAssistantIdRef.current &&
                messages[lastAssistantIndex]?.id ===
                  thinkingAssistantIdRef.current;

              const elements: React.ReactNode[] = [];

              // Helper to safely parse tool content JSON
              const safeParseToolContent = (
                content: string | undefined
              ): { action?: string; toolName?: string } | null => {
                if (!content) return null;
                try {
                  const p = JSON.parse(content);
                  return { action: p.action, toolName: p.toolName };
                } catch {
                  return null;
                }
              };

              // Render a single message with thinking block logic
              const renderSingleMessage = (
                message: ChatMessage,
                idx: number
              ) => {
                const isAssistant = message.role === "assistant";
                const isLastAssistant =
                  isAssistant && idx === lastAssistantIndex;
                const showStreamingPlain = isLastAssistant && isLoading;

                // Insert live streaming ThinkingBlock before the last assistant
                // message ONLY if it's the message the thinking block belongs to.
                if (
                  isLastAssistant &&
                  thinkingBelongsToLastAssistant &&
                  thinkingBlockElement
                ) {
                  elements.push(thinkingBlockElement);
                }

                // For persisted messages with thinkingContent (loaded from history),
                // render a ThinkingBlock above the assistant message — but only if
                // this message is NOT the one currently being streamed (to avoid
                // double-rendering with the live thinkingBlockElement above).
                if (
                  isAssistant &&
                  message.thinkingContent &&
                  !hideReasoning &&
                  !(isLastAssistant && thinkingBelongsToLastAssistant)
                ) {
                  elements.push(
                    <div
                      key={`thinking-persisted-${message.id}`}
                      className="patent-draft-agent-message assistant-message"
                    >
                      <div className="patent-draft-agent-message-container">
                        <div className="patent-draft-agent-text">
                          <ThinkingBlock
                            thinkingContent={message.thinkingContent}
                            isThinking={false}
                            durationSeconds={message.thinkingDuration}
                          />
                        </div>
                      </div>
                    </div>
                  );
                }

                elements.push(
                  <MessageItem
                    key={message.id}
                    message={message as Message}
                    isStreaming={showStreamingPlain}
                    onChoiceSelect={handleChoiceSelect}
                    answeredChoices={answeredChoices.answered}
                    activePendingChoices={
                      activePendingChoices?.messageId === message.id
                        ? activePendingChoices
                        : null
                    }
                  />
                );
              };

              // Group consecutive tool result messages with the same toolName,
              // then render groups as collapsible and singles as before.
              let i = 0;
              while (i < messages.length) {
                const message = messages[i];

                // Check if this is a groupable tool result message
                if (message.role === "tool") {
                  const parsed = safeParseToolContent(message.content);
                  if (
                    parsed?.action === "tool_result" &&
                    parsed.toolName &&
                    parsed.toolName !== "write_todos"
                  ) {
                    // Collect consecutive tool results with the same toolName
                    const toolName = parsed.toolName;
                    const group: ChatMessage[] = [message];
                    while (i + 1 < messages.length) {
                      const next = messages[i + 1];
                      const nextParsed = safeParseToolContent(next?.content);
                      if (
                        next?.role === "tool" &&
                        nextParsed?.action === "tool_result" &&
                        nextParsed.toolName === toolName
                      ) {
                        group.push(next);
                        i++;
                      } else {
                        break;
                      }
                    }

                    if (group.length > 1) {
                      // Render as a collapsed group
                      elements.push(
                        <CollapsedToolGroup
                          key={`tool-group-${group[0].id}`}
                          toolName={toolName}
                          messages={group as Message[]}
                        />
                      );
                    } else {
                      // Single item — render normally
                      renderSingleMessage(message, i);
                    }
                    i++;
                    continue;
                  }
                }

                // Non-groupable message — render normally
                renderSingleMessage(message, i);
                i++;
              }

              // If thinking block exists but wasn't placed before an assistant
              // message (still thinking, or text hasn't started yet), show at bottom
              if (thinkingBlockElement && !thinkingBelongsToLastAssistant) {
                elements.push(thinkingBlockElement);
              }

              return elements;
            })()
          )}

          {/* Loading indicator - appears before streaming starts (only when not thinking) */}
          {isLoading && !streamingStarted && !isThinking && (
            <LoadingIndicator />
          )}
          {footerContent}
        </div>

        {/* Scroll to bottom button */}
        {showScrollToBottom && (
          <Button
            variant="primary"
            size="sm"
            onClick={scrollToBottom}
            className="patent-draft-agent-scroll-to-bottom-btn"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path d="M8 1a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 .708-.708L7.5 13.293V1.5A.5.5 0 0 1 8 1z" />
            </svg>
          </Button>
        )}

        {/* In read-only mode, the main input is hidden, but we still need the chat toggler */}
        {readOnly && availableChats.length > 0 && (
          <div
            className="patent-draft-agent-chat-input-container"
            style={{
              padding: "12px 16px",
              borderTop: "1px solid #dee2e6",
              backgroundColor: "#f8f9fa",
              display: "flex",
              justifyContent: "flex-start",
              borderBottomLeftRadius: "12px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span
                style={{ fontSize: "14px", fontWeight: 500, color: "#495057" }}
              >
                {t(
                  "documentAgentChat.viewingConversation",
                  "Viewing conversation:"
                )}
              </span>
            </div>
          </div>
        )}

        {/* Input form - hidden in readOnly mode */}
        {!readOnly && (
          <div className="patent-draft-agent-chat-input-container">
            {initialError && (
              <div role="alert" className="alert alert-warning">
                {initialError}
              </div>
            )}
            {!minimalChrome && !consumerMode && (
              <PinnedSkills workproductId={workproductId} readOnly={readOnly} />
            )}
            <Form onSubmit={handleSubmitStream} className="d-flex flex-column">
              {/* Image previews */}
              {selectedImages.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                    marginBottom: 8,
                  }}
                >
                  {selectedImages.map((item, idx) => (
                    <div key={idx} style={{ position: "relative" }}>
                      <img
                        src={item.previewUrl}
                        alt={`upload-${idx}`}
                        style={{
                          width: 64,
                          height: 64,
                          objectFit: "cover",
                          borderRadius: 6,
                          border: "1px solid #dee2e6",
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(idx)}
                        style={{
                          position: "absolute",
                          top: -6,
                          right: -6,
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          border: "none",
                          background: "#dc3545",
                          color: "#fff",
                          cursor: "pointer",
                        }}
                        aria-label="Remove image"
                      >
                        <span
                          style={{
                            position: "relative",
                            top: -4,
                            left: -1,
                            display: "block",
                          }}
                        >
                          ×
                        </span>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Round 3: plan-mode proposal review panel */}
              {pendingProposals && pendingProposals.length > 0 && (
                <div
                  className="patent-draft-agent-proposals-panel"
                  style={{
                    border: "1px solid #d8e1ec",
                    background: "#f8fafc",
                    borderRadius: 8,
                    padding: "10px 12px",
                    marginBottom: 8,
                    fontSize: 13,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 6,
                    }}
                  >
                    <strong>
                      {t(
                        "documentAgentChat.proposalsHeading",
                        "{{count}} proposed change(s)",
                        { count: pendingProposals.length }
                      )}
                    </strong>
                    <div style={{ display: "flex", gap: 6 }}>
                      <Button
                        size="sm"
                        variant="success"
                        disabled={proposalsActionInFlight}
                        onClick={async () => {
                          if (!chatId) return;
                          setProposalsActionInFlight(true);
                          try {
                            const resp = await fetch(
                              `${backendUrl}/api/agent/proposals/approve`,
                              {
                                method: "POST",
                                credentials: "include",
                                headers: {
                                  "Content-Type": "application/json",
                                },
                                body: JSON.stringify({
                                  threadId: chatId,
                                  workproductId,
                                  documentContext: getContextForAgent(),
                                  proposalIds: pendingProposals.map(p => p.id),
                                }),
                              }
                            );
                            if (resp.ok) {
                              // Patches are persisted on the backend; the
                              // chat's existing patch fetcher will pick
                              // them up on the next stream open. For
                              // immediate update, send any short follow-up.
                              setPendingProposals(null);
                            }
                          } finally {
                            setProposalsActionInFlight(false);
                          }
                        }}
                      >
                        {t(
                          "documentAgentChat.proposalsApproveAll",
                          "Approve all"
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={proposalsActionInFlight}
                        onClick={async () => {
                          if (!chatId) return;
                          setProposalsActionInFlight(true);
                          try {
                            await fetch(
                              `${backendUrl}/api/agent/proposals/reject`,
                              {
                                method: "POST",
                                credentials: "include",
                                headers: {
                                  "Content-Type": "application/json",
                                },
                                body: JSON.stringify({
                                  threadId: chatId,
                                  workproductId,
                                  proposalIds: pendingProposals.map(p => p.id),
                                }),
                              }
                            );
                            setPendingProposals(null);
                          } finally {
                            setProposalsActionInFlight(false);
                          }
                        }}
                      >
                        {t(
                          "documentAgentChat.proposalsRejectAll",
                          "Reject all"
                        )}
                      </Button>
                    </div>
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {pendingProposals.map(p => (
                      <li key={p.id} style={{ marginBottom: 4 }}>
                        <code
                          style={{
                            background: "#eef2f6",
                            padding: "1px 4px",
                            borderRadius: 3,
                          }}
                        >
                          {p.toolName}
                        </code>
                        {p.rationale ? (
                          <span style={{ color: "#475569" }}>
                            {" "}
                            — {p.rationale}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Chat input box container */}
              <div className="patent-draft-agent-input-box">
                <Form.Control
                  as="textarea"
                  value={inputValue}
                  onChange={handleTextareaChange}
                  onKeyDown={handleKeyDown}
                  onCompositionStart={() => setIsComposing(true)}
                  onCompositionEnd={() => setIsComposing(false)}
                  placeholder={
                    inputDisabled && inputDisabledMessage
                      ? inputDisabledMessage
                      : t("documentAgentChat.typeMessageHere")
                  }
                  rows={textareaRows}
                  className="patent-draft-agent-textarea"
                  disabled={isLoading || inputDisabled}
                  style={{ resize: "none" }}
                />

                {/* Actions row: controls on left, submit button on right */}
                <div
                  className="patent-draft-agent-input-actions-row"
                  ref={actionsRowRef}
                >
                  {/* Chat management controls */}
                  <div className="patent-draft-agent-chat-controls">
                    {/* Icon buttons shown inline when enough space */}
                    {!chatControlsHidden && (
                      <>
                        {renderUploadDropdown()}
                        {renderShareButton()}
                      </>
                    )}

                    {/* Overflow three-dots button (shown when icons don't fit) */}
                    {chatControlsHidden && (
                      <div
                        className="chat-controls-overflow"
                        ref={chatOverflowRef}
                      >
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => setShowChatOverflow(!showChatOverflow)}
                          className="chat-controls-overflow-btn"
                          title={t(
                            "documentAgentChat.moreOptions",
                            "More options"
                          )}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            fill="currentColor"
                            viewBox="0 0 16 16"
                          >
                            <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z" />
                          </svg>
                        </Button>
                        {showChatOverflow && (
                          <div className="chat-controls-overflow-popup">
                            {renderUploadDropdown()}
                            {renderShareButton()}
                            {modelSwitcherHidden &&
                              !consumerMode &&
                              renderModelSelector()}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Model selector (pinned, visible unless very narrow) */}
                    {!modelSwitcherHidden &&
                      !consumerMode &&
                      renderModelSelector()}

                    {/* Hidden file inputs (always in DOM) */}
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.jpe,.webp"
                      multiple
                      hidden
                      onChange={handlePickImages}
                    />
                    <input
                      ref={docxInputRef}
                      type="file"
                      accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      hidden
                      onChange={handleImportDocx}
                    />
                    <input
                      ref={pdfInputRef}
                      type="file"
                      accept=".pdf,application/pdf"
                      hidden
                      onChange={handleImportPdf}
                    />
                  </div>

                  {/* Mode + Send grouped — the dropdown sits flush against
                      the send button. */}
                  <div
                    className="patent-draft-agent-mode-send-group"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    {contextUsage && (
                      <ContextGauge
                        contextTokens={contextUsage.contextTokens}
                        maxContextTokens={contextUsage.maxContextTokens}
                      />
                    )}
                    {renderAgentModeSelector()}

                    {/* Submit / Stop button (always visible) */}
                    <Button
                      variant="primary"
                      type={isLoading ? "button" : "submit"}
                      className="patent-draft-agent-submit-button"
                      disabled={
                        inputDisabled ||
                        (!isLoading &&
                          inputValue.trim() === "" &&
                          selectedImages.length === 0)
                      }
                      title={
                        isLoading
                          ? t("documentAgentChat.stop")
                          : t("documentAgentChat.send")
                      }
                      onClick={isLoading ? handleStopGenerating : undefined}
                    >
                      {isLoading ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="12"
                          viewBox="0 0 16 16"
                        >
                          <rect
                            x="2"
                            y="2"
                            width="12"
                            height="12"
                            fill="#FFFFFF"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="12"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M15.854.146a.5.5 0 0 1 .11.54l-5.819 14.547a.75.75 0 0 1-1.329.124l-3.178-4.995L.643 7.184a.75.75 0 0 1 .124-1.33L15.314.037a.5.5 0 0 1 .54.11ZM6.636 10.07l2.761 4.338L14.13 2.576 6.636 10.07Zm6.787-8.201L1.591 6.602l4.339 2.76 7.494-7.493Z" />
                        </svg>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </Form>
            {extraActions && (
              <div className="rv-chat-review-action">{extraActions}</div>
            )}
          </div>
        )}
      </div>

      {/* Confirm a model switch that would force an immediate summarization */}
      <Modal
        show={!!pendingModelSwitch}
        onHide={cancelModelSwitch}
        centered
        className="workspace-theme wp-dialog-editor patent-draft-agent-delete-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("documentAgentChat.switchModelTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {t("documentAgentChat.switchModelCompactWarning", {
              model: pendingModelSwitch
                ? MODEL_DISPLAY_NAMES[pendingModelSwitch]
                : "",
            })}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button className="btn-cancel" onClick={cancelModelSwitch}>
            {t("documentAgentChat.cancel")}
          </Button>
          <Button variant="primary" onClick={confirmModelSwitch}>
            {t("documentAgentChat.switchModelConfirm")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Share Modal */}
      <ShareableReportModal
        show={showShareModal}
        onHide={() => setShowShareModal(false)}
        isShared={isPublic}
        workproductId={workproductId}
        onToggleSharing={handleToggleShare}
        isLoading={isTogglingShare}
        shareUrl={shareUrl || undefined}
      />
    </div>
  );
};

export default DocumentAgentLGChat;
